<template>
	<div class="app-sidebar-favorites pa-4">
		<div class="px-2 mb-4 ao-p2-medium d-flex align-center">
			<span
				class="mr-2"
				style="color: #b6bbc2"
			>
				{{ $t('My links') }}
			</span>

			<v-menu
				v-model="isActiveSettingsMenu"
				:close-on-content-click="false"
			>
				<template v-slot:activator="{ props }">
					<ao-icon-btn
						v-bind="props"
						iconName="cog"
						variant="text"
						size="20"
					/>
				</template>
				<v-list class="pa-2">
					<v-menu
						v-model="isActiveCreateGroupMenu"
						:close-on-content-click="false"
						location="right"
					>
						<template v-slot:activator="{ props: createGroupMenuProps }">
							<ao-btn
								v-bind="createGroupMenuProps"
								prependIconName="plus"
								custom="favoriteSettingMenuItem"
							>
								{{ $t('Create group') }}
							</ao-btn>
						</template>
						<FavoriteGroupForm
							@saveGroup="onCreateGroup"
							@cancel="isActiveCreateGroupMenu = false"
						/>
					</v-menu>
				</v-list>
			</v-menu>
		</div>

		<v-list>
			<draggable
				:list="favoritesLinks.groups"
				item-key="key"
				@change="onChangeGroupOrdering"
			>
				<template v-slot:item="{ element }">
					<v-list-group
						:value="element.key"
						collapse-icon=""
						expand-icon=""
						fluid
					>
						<template v-slot:activator="{ props: listGroupProps, isOpen }">
							<FavoriteGroupItem
								:element="element"
								:isOpen="isOpen"
								:listGroupProps="listGroupProps"
								@update="onUpdateGroup"
								@delete="onDeleteGroup"
							/>
						</template>
						<draggable
							:list="element.links"
							group="links"
							item-key="key"
							@change="onChangeLinkOrdering"
						>
							<template v-slot:item="{ element: link }">
								<FavoriteLinkItem
									:link="link"
									:groups="groups"
									:currentGroupKey="element.key"
									@update="onUpdateLink"
									@delete="onDeleteLink"
								/>
							</template>
						</draggable>
						<div
							v-if="element.links.length === 0"
							class="py-2 text-center ao-p3-regular text-textSecondary"
						>
							{{ $t('No links') }}
						</div>
					</v-list-group>
				</template>
			</draggable>
		</v-list>

		<div class="text-textSecondary mt-6 mb-2 ao-p2-medium px-2">
			{{ $t('Other links') }}
		</div>

		<draggable
			:list="favoritesLinks.ungrouped"
			item-key="id"
			group="links"
			@change="onChangeLinkOrdering"
		>
			<template v-slot:item="{ element }">
				<div>
					<FavoriteLinkItem
						:link="element"
						:groups="groups"
						@update="onUpdateLink"
						@delete="onDeleteLink"
					/>
				</div>
			</template>
		</draggable>

		<confirm-dialog ref="ConfirmComponent" />
	</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import draggable from 'vuedraggable'
import { useI18n } from 'vue-i18n'

import { useCompanyFavoriteLinksStore } from '@/stores/companyFavoriteLinksStore'

import FavoriteGroupForm from '@/components/favorites/FavoriteGroupForm.vue'
import FavoriteGroupItem from '@/components/favorites/FavoriteGroupItem.vue'
import FavoriteLinkItem from '@/components/favorites/FavoriteLinkItem.vue'

const { t: $t } = useI18n()

const ConfirmComponent = ref(null)

const companyFavoriteLinksStore = useCompanyFavoriteLinksStore()
const { favoritesLinks } = storeToRefs(companyFavoriteLinksStore)
const {
	getFirstOrCreateFavoritesPreset,
	createFavoriteGroup,
	changeFavoriteGroupName,
	deleteFavoriteGroup,
	changeFavoriteLink,
	deleteFavoriteLink,
	onChangeOrdering,
} = companyFavoriteLinksStore

const groups = computed(() => {
	return favoritesLinks.value.groups.map((group) => {
		return {
			name: group.name,
			key: group.key,
		}
	})
})

const isActiveSettingsMenu = ref(false)
const isActiveCreateGroupMenu = ref(false)

const onCreateGroup = (payload) => {
	createFavoriteGroup(payload).then(() => {
		isActiveCreateGroupMenu.value = false
	})
}

const onUpdateGroup = (payload) => {
	// console.log('onUpdateGroup', payload)
	changeFavoriteGroupName(payload.key, payload.name)
}

const deletedGroupKey = ref(null)
const onDeleteGroup = (group) => {
	const desc = $t('Your group will be deleted and all items from it will be moved to the Other Links section')
	ConfirmComponent.value
		.open(
			$t('Delete group [name]?', { name: group.name }),
			desc,
			'delete'
		)
		.then((result) => {
			if (result) {
				deletedGroupKey.value = group.key
				deleteFavoriteGroup(group.key).then(() => {
					deletedGroupKey.value = null
				})
			}
		})
}

const onUpdateLink = ({ groupKey, linkKey, payload }) => {
	// console.log('onUpdateLink', payload)
	changeFavoriteLink(groupKey, linkKey, payload)
}

const onDeleteLink = ({ groupKey, linkKey, title }) => {
	ConfirmComponent.value
		.open(
			$t('Delete link [name]', { name: title }),
			null,
			'delete'
		)
		.then((result) => {
			if (result) {
				deleteFavoriteLink(groupKey, linkKey)
			}
		})
}

const onChangeGroupOrdering = () => {
	onChangeOrdering()
}

const onChangeLinkOrdering = () => {
	// console.log('onChangeLinkOrdering')
	onChangeOrdering()
}

onMounted(() => {
	// console.log('onMounted SidebarFavorites')
	getFirstOrCreateFavoritesPreset()
})
</script>
